import { DungeonEntity } from '../entities/DungeonEntity';
import type { EffectStep } from './effect.types';
import type { EntityEvent } from './buff.types';

export type ItemEffect = EffectStep;

/**
 * Event listener attached to an item. When the matched event is emitted on
 * the player (see `emitEntityEvent`), the handler runs with `_itemSlot`
 * injected into the params so it can manipulate the source stack (typically
 * `consumeSelf`).
 *
 * If `requiresActivation` is true, the listener is inert until
 * `InventorySlot.activated` is true (toggle UI Phase 11+).
 *
 * `priority` determines the execution order **across listeners from several
 * items**: higher value fires earlier. Default 0. Lets a signature item
 * (e.g. escape-seed) take precedence over a basic auto-revive. See the
 * `onDeath` short-circuit in `emitEntityEvent`. Within a single item the
 * order of the `listeners` array is preserved (stable sort) — give the same
 * priority to a chain of dependent effects.
 */
export interface ItemListener extends EffectStep {
  event: EntityEvent;
  requiresActivation?: boolean;
  priority?: number;
}

export enum ItemType {
  MISC = 'misc',
  MATERIAL = 'material',
  CONSUMABLE = 'consumable',
  KEY = 'key',
  BOOST = 'boost',
}

/**
 * Material rarity tier (see docs/game-design/loot.md). Determines drop
 * sources: common = basic mobs + locations, thematic = several themed
 * dungeons, signature = a single dungeon (specific boss/elite/location),
 * unique = an ultra-rare boss/location.
 * Relevant only when `ItemDefinition.type === MATERIAL`.
 */
export type MaterialTier = 'common' | 'thematic' | 'signature' | 'unique';

/** Static definition of an item (immutable catalogue entry). */
export interface ItemDefinition {
  id: string;
  type: ItemType;
  label: string;
  maxStack: number;
  /** Filename of the item icon (without extension). */
  sprite: string;
  /** Icon frame name overlaid bottom-right of the sprite (e.g. the spell icon on a grimoire). */
  iconOverlay?: string;
  /** Effects triggered on active use (`InventoryService.useConsumable`). */
  effects?: ItemEffect[];
  description?: string;
  /** Modifier id for `ItemType.BOOST` items — identifies which modifier this boost carries. */
  modifierId?: string;
  /**
   * Resell value at the hub (see docs/game-design/loot.md "Tout item
   * est vendable"). Placeholder Phase 5 — calibration Phase 11.
   */
  sellValue: number;
  /** Rarity tier for materials. Ignored for other types. */
  materialTier?: MaterialTier;
  /**
   * Passive listeners triggered by the player's entity events (onDeath,
   * onAttack, onDamageTaken, etc.). See `docs/items.md` and
   * `src/utils/itemListener.utils.ts`. Mirror pattern of `BuffHook`.
   */
  listeners?: ItemListener[];
}

/** A single inventory slot (one item type per slot). */
export interface InventorySlot {
  itemId: string;
  quantity: number;
  /**
   * User activation state for listeners with `requiresActivation`.
   * Absent / false = listener inert. Toggle UI = polish Phase 11.
   */
  activated?: boolean;
}

/** Serialisable inventory state (for localStorage saves). */
export interface InventorySaveData {
  slots: (InventorySlot | null)[];
  /** Carried gold — a separate counter, not an inventory slot. */
  gold?: number;
}

export type GroundItemMode = 'possessed' | 'common';

export interface GroundItem {
  id: string;
  itemId: string;
  x: number;
  y: number;
  quantity: number;
  /** Possessed = private to the owning browser (never replicated). Common =
   *  host-authoritative thrown item (Plan C). Defaults to possessed. */
  mode: GroundItemMode;
  /** Common items only: the PeerId that threw it (anti-immediate-repickup). */
  dropperId?: string;
  /** Common items only: the dropper may re-pick it only once armed (they left
   *  its pickup range since the drop). Latch — not serialized. */
  armed?: boolean;
}

export interface PickupNotification {
  id: string;
  itemId: string;
  quantity: number;
  /** World-space position where the notification spawns (pixels). */
  worldX: number;
  worldY: number;
  /** Elapsed ticks since the notification was queued. */
  elapsed: number;
  /** Ticks to wait before becoming visible (stagger). */
  delay: number;
  /** Total visible duration in ticks (after delay elapses). */
  duration: number;
}

export interface FlyingItem {
  id: string;
  itemId: string;
  quantity: number;
  entity: DungeonEntity;
  /** World-space start position (pixels). */
  startX: number;
  startY: number;
  /** Current interpolated world-space position (pixels). */
  currentX: number;
  currentY: number;
  /** 0 → 1 animation progress. */
  progress: number;
  /** Total animation duration in ticks. */
  duration: number;
}
